/* ==========================================================================
   OVIA - pergola bundle: two $0 gifts + opt-out shipping protection.

   Shopper-visible behaviour:
     - A pergola in the cart  -> the gift sun shade IN THE MATCHING SIZE and
       the gift rain sensor sit in the cart at quantity 1 each, shown as
       gifts and not editable, and Shipping Protection ($150) is in the cart
       unless the shopper switched it off during this browsing session.
     - Switch the pergola to another size -> the wrong-size shade is removed
       and the right one added in the SAME /cart/update.js call.
     - No pergola in the cart -> all of those lines are removed.

   It also republishes window.oviaShadeSize, the contract that
   blocks/_product-price.liquid depends on to keep its PDP gift strip in
   step with the selected pergola.

   Additive only: it does not modify assets/cart.js or
   assets/cart-components.js. It reads /cart.js, writes once through
   /cart/update.js, and re-renders through the same section-rendering path
   the theme itself uses:
     drawer    -> section "minicart-form", swapped into #minicart-form
       (what CartNotification.updateQuantity does)
     cart page -> #main-cart-items .js-contents + .cart-info .totals
       (what CartUtils.updateCartUI does)
   A MutationObserver on those two containers re-applies the UI after every
   re-render, so the theme replacing innerHTML never strips the gift styling.
   Every write is conditional - see "LOOP GUARD" below.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__oviaBundle) return;
  window.__oviaBundle = true;

  /* ======================================================================
     CONFIG - the only part the owner should need to edit.
     ====================================================================== */

  /* A cart line whose product_id appears here means "a pergola is in the
     cart". Add a product id to this array to extend the bundle to a new
     pergola model.

     Why product ids and not tags or product_type:
       - /cart.js line items do not expose product tags at all, so tag-based
         detection is not possible on the storefront.
       - product_type is NOT safe here: several of this store's custom
         invoice products also carry the type "Pergola" and would wrongly
         trigger the gifts. */
  var TRIGGER_PRODUCT_IDS = [
    8247932846215, // OVIA Aluminum T5 Pergola Ultra
    8247932878983  // OVIA T6 Aluminum Heavy Duty Pergola Max+
  ];

  /* The gift sun shade has one variant per size; exactly one of them belongs
     in the cart, chosen by sizeFromText() below. compareAt is in cents and
     drives the struck price in the cart; keep it in step with the variant's
     compare-at price in Shopify, because /cart.js does not expose
     compare_at_price. */
  var SHADE = {
    10: { variant: 46237631643783, compareAt: 98000 },  // GIFT-SHADE-10, $980
    13: { variant: 46237631676551, compareAt: 118000 }  // GIFT-SHADE-13, $1,180
  };

  /* Used when a pergola is in the cart but its size cannot be read. */
  var SHADE_DEFAULT_SIZE = 10;

  /* Gift rain sensor - one variant, no size. */
  var SENSOR = { variant: 46236347924615, compareAt: 39500 };

  /* Shipping Protection - a real, charged line. On by default, one click off. */
  var PROTECTION = {
    variant: 42748102049927,
    price: 15000,
    title: 'Shipping Protection',
    note: "Protects your order while it's in transit. Switch it off if you don't want it."
  };

  var GIFT_BADGE = 'Gift';

  /* Variants this script used to manage and must now clear out of any cart
     that still carries one. 46236347531399 was the single-variant gift shade
     before it was split by size; it no longer exists in Shopify, so it can
     never be added again - only removed. */
  var RETIRED_VARIANTS = [46236347531399];

  /* ======================================================================
     Small helpers
     ====================================================================== */

  var MANAGED = [SHADE[10].variant, SHADE[13].variant, SENSOR.variant,
                 PROTECTION.variant].concat(RETIRED_VARIANTS);

  var GIFT_BY_VARIANT = {};
  GIFT_BY_VARIANT[SHADE[10].variant] = SHADE[10];
  GIFT_BY_VARIANT[SHADE[13].variant] = SHADE[13];
  GIFT_BY_VARIANT[SENSOR.variant] = SENSOR;

  var cartRoot = (window.routes && window.routes.cart_url) || '/cart';

  function money(cents) {
    var whole = Math.round(cents / 100);
    return '$' + String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ---- the size rule ----------------------------------------------------
     Lifted from snippets/ovia-free-shade.liquid, which has been running this
     offer since 3 October. Its sizeOf() was:

       var m = raw.split('|')[0].match(/(\d+)'?\s*x\s*(\d+)/i); if (!m) return null;
       return Math.min(parseInt(m[1], 10), parseInt(m[2], 10)) >= 13 ? 13 : 10;

     So: read the pergola's Size option as "A x B" and take the SHORTER side;
     13 ft or more gets the 13 ft shade, anything less gets the 10 ft one.
     That is why 10' x 13' gets the 10 FT shade while 13' x 19' gets the 13 ft
     one - a substring test for "13" would get 10' x 13' wrong, and both
     pergolas sell that size. The "4 Posts " / "6 Posts " prefix on some Ultra
     sizes is harmless because the pattern requires a literal x between the
     two numbers. Checked against all 13 Size values the two products carry:
     none falls through. The regex is kept character-for-character. */
  var SIZE_RE = /(\d+)'?\s*x\s*(\d+)/i;

  function sizeFromText(text) {
    var m = SIZE_RE.exec(String(text == null ? '' : text));
    if (!m) return null;
    return Math.min(parseInt(m[1], 10), parseInt(m[2], 10)) >= 13 ? 13 : 10;
  }

  /* The size the cart implies, or null when no pergola is in it. The first
     pergola line in cart order decides, which is what ovia-free-shade did
     (it broke out of its loop on the first pergola it could size). */
  function pergolaSizeFromCart(items) {
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (TRIGGER_PRODUCT_IDS.indexOf(it.product_id) === -1) continue;
      var owv = it.options_with_values || [];
      for (var j = 0; j < owv.length; j++) {
        if (String(owv[j] && owv[j].name || '').toLowerCase() === 'size') {
          var byName = sizeFromText(owv[j].value);
          if (byName) return byName;
        }
      }
      var vo = it.variant_options || [];
      for (var k = 0; k < vo.length; k++) {
        var byPos = sizeFromText(vo[k]);
        if (byPos) return byPos;
      }
      var byTitle = sizeFromText(it.variant_title);
      if (byTitle) return byTitle;
      /* A pergola we cannot size: fall back rather than treat the cart as
         having no pergola at all, which would strip the whole bundle. */
      return SHADE_DEFAULT_SIZE;
    }
    return null;
  }

  function log(msg, data) {
    if (window.__oviaBundleDebug) console.log('[ovia-bundle] ' + msg, data || '');
  }

  /* ---- shipping-protection opt-out --------------------------------------
     sessionStorage, so it lasts for the browsing session and is forgotten
     afterwards. A private window or blocked site data makes storage throw,
     so the answer is also mirrored in memory for this page load. */
  var OPT_OUT_KEY = 'ovia:shipping-protection:off';
  var optOutMemory = null;

  function protectionOff() {
    if (optOutMemory !== null) return optOutMemory;
    try { return sessionStorage.getItem(OPT_OUT_KEY) === '1'; } catch (e) { return false; }
  }

  function setProtectionOff(off) {
    optOutMemory = !!off;
    try {
      if (off) sessionStorage.setItem(OPT_OUT_KEY, '1');
      else sessionStorage.removeItem(OPT_OUT_KEY);
    } catch (e) { /* storage blocked - memory copy still holds for this page */ }
  }

  /* ======================================================================
     LOOP GUARD

     The failure mode this protects against: we write to the cart, the theme
     re-renders, our observer sees the mutation, we write again, forever.

     Three independent brakes, in order of importance:

     1. EVERY WRITE IS CONDITIONAL. buildPlan() computes the desired quantity
        for each of the managed variants and compares it against the
        quantity actually in the cart right now. plan.updates is null unless
        something differs, and write() is never called when it is null. So
        the re-render our own write provokes costs exactly one extra
        /cart.js read and then stops. This alone makes the loop terminate.

     2. IN-FLIGHT MUTEX. `inFlight` is held from before the /cart.js read
        until the write finishes, so two observer bursts cannot both read a
        stale cart and then both write. Anything that arrives while a write
        is in flight sets `queued`, and exactly one follow-up tick runs
        afterwards.

     3. NON-CONVERGENCE CIRCUIT BREAKER. If the cart never reaches the state
        we ask for - a gift goes out of stock, an app rewrites the cart
        behind us, /cart/update.js refuses to add a variant - then brake 1
        never fires and we would retry forever. So: an identical updates
        payload sent 3 times in a row, with no converged tick in between,
        halts the feature for the rest of the page load. The counter is
        reset by any tick that finds nothing to do, which is what separates
        "not converging" from "the shopper keeps undoing us". A rolling rate
        limit (10 writes inside 5s) is a second, blunter version of this.
     ====================================================================== */

  var inFlight = false;       // brake 2
  var queued = false;         // brake 2
  var halted = false;         // brake 3
  var lastSignature = '';     // brake 3
  var identicalWrites = 0;    // brake 3
  var writeTimes = [];        // brake 3 (rolling)
  var useAddFallback = false; // see write()
  var timer = null;

  var MAX_IDENTICAL_WRITES = 3;
  var RATE_WINDOW_MS = 5000;
  var RATE_MAX_WRITES = 10;

  function schedule(delay) {
    if (halted) return;
    clearTimeout(timer);
    timer = setTimeout(tick, typeof delay === 'number' ? delay : 120);
  }

  function release() {
    inFlight = false;
    if (queued) { queued = false; schedule(60); }
  }

  /* ======================================================================
     Reading the cart and deciding what to do
     ====================================================================== */

  function getCart() {
    return fetch(cartRoot + '.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json', 'X-Ovia-Bundle': '1' }
    }).then(function (r) { return r.json(); });
  }

  function buildPlan(cart) {
    var items = (cart && cart.items) || [];

    /* null when there is no pergola; otherwise 10 or 13. */
    var size = pergolaSizeFromCart(items);
    var pergola = size !== null;

    // Quantity actually in the cart, summed over every line that uses the
    // variant (a variant can appear on more than one line).
    var current = {};
    MANAGED.forEach(function (id) { current[id] = 0; });
    items.forEach(function (i) {
      if (Object.prototype.hasOwnProperty.call(current, i.variant_id)) {
        current[i.variant_id] += i.quantity;
      }
    });

    /* Exactly one shade size is wanted, so the other is explicitly wanted at
       zero. On a size switch that makes updates carry {wrong: 0, right: 1},
       which goes out as ONE /cart/update.js call - not a remove then an add. */
    var desired = {};
    desired[SHADE[10].variant] = (pergola && size === 10) ? 1 : 0;
    desired[SHADE[13].variant] = (pergola && size === 13) ? 1 : 0;
    desired[SENSOR.variant] = pergola ? 1 : 0;
    desired[PROTECTION.variant] = (pergola && !protectionOff()) ? 1 : 0;
    /* Never re-added, only cleared. */
    RETIRED_VARIANTS.forEach(function (id) { desired[id] = 0; });

    // Brake 1: only the differences, and null when there are none.
    var updates = null;
    MANAGED.forEach(function (id) {
      if (current[id] !== desired[id]) {
        if (!updates) updates = {};
        updates[id] = desired[id];
      }
    });

    return {
      cart: cart, items: items, pergola: pergola, size: size,
      current: current, desired: desired, updates: updates
    };
  }

  function tick() {
    if (halted || inFlight) { if (inFlight) queued = true; return; }
    inFlight = true;

    getCart().then(function (cart) {
      var plan = buildPlan(cart);
      decorate(plan);

      if (!plan.updates) {
        // Converged. Reset the non-convergence counter (brake 3) so that a
        // shopper repeatedly undoing us is not mistaken for a stuck loop.
        identicalWrites = 0;
        lastSignature = '';
        release();
        return;
      }
      return write(plan);
    }).catch(function (e) {
      log('tick failed', e);
      release();
    });
  }

  /* ======================================================================
     Writing
     ====================================================================== */

  function sectionsToRequest() {
    var list = [];
    if (document.getElementById('minicart-form')) list.push('minicart-form');
    var main = document.getElementById('main-cart-items');
    if (main && main.dataset.id) list.push(main.dataset.id);
    return list;
  }

  function postJSON(url, payload) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Ovia-Bundle': '1'
      },
      body: JSON.stringify(payload)
    }).then(function (r) { return r.json(); });
  }

  function write(plan) {
    var sig = JSON.stringify(plan.updates);

    if (sig === lastSignature) {
      identicalWrites++;
      // Second identical attempt: /cart/update.js apparently did not add the
      // missing variant. Switch additions to /cart/add.js for the retry.
      if (identicalWrites === 2) {
        useAddFallback = true;
        log('switching additions to /cart/add.js');
      }
      if (identicalWrites >= MAX_IDENTICAL_WRITES) {
        halted = true;
        log('halted: cart is not converging on', plan.updates);
        release();
        return;
      }
    } else {
      lastSignature = sig;
      identicalWrites = 1;
    }

    var now = Date.now();
    writeTimes = writeTimes.filter(function (t) { return now - t < RATE_WINDOW_MS; });
    if (writeTimes.length >= RATE_MAX_WRITES) {
      halted = true;
      log('halted: write rate limit');
      release();
      return;
    }
    writeTimes.push(now);

    var sections = sectionsToRequest();
    var url = (window.routes && window.routes.cart_update_url) || cartRoot + '/update.js';
    var addUrl = (window.routes && window.routes.cart_add_url) || cartRoot + '/add.js';

    // Normal path: ONE /cart/update.js, updates keyed by variant id. No
    // sequential /cart/add.js calls, so the cart can never be half applied.
    if (!useAddFallback) {
      return postJSON(url, {
        updates: plan.updates,
        sections: sections,
        sections_url: window.location.pathname
      }).then(applyResponse).catch(function (e) {
        log('update failed', e);
        release();
      });
    }

    // Fallback path, only after /cart/update.js demonstrably failed to add a
    // variant: additions through /cart/add.js, everything else still in one
    // /cart/update.js. At most two calls.
    var adds = [];
    var sets = {};
    Object.keys(plan.updates).forEach(function (id) {
      if (plan.updates[id] > 0 && plan.current[id] === 0) {
        adds.push({ id: Number(id), quantity: plan.updates[id] });
      } else {
        sets[id] = plan.updates[id];
      }
    });
    var hasSets = Object.keys(sets).length > 0;

    var first = adds.length
      ? postJSON(addUrl, {
          items: adds,
          sections: hasSets ? [] : sections,
          sections_url: window.location.pathname
        })
      : Promise.resolve(null);

    return first.then(function (r) {
      if (!hasSets) return r;
      return postJSON(url, {
        updates: sets,
        sections: sections,
        sections_url: window.location.pathname
      });
    }).then(applyResponse).catch(function (e) {
      log('fallback failed', e);
      release();
    });
  }

  /* ======================================================================
     Re-rendering through the theme's own section path
     ====================================================================== */

  function innerFrom(html, selector) {
    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var el = doc.querySelector(selector);
      return el ? el.innerHTML : null;
    } catch (e) { return null; }
  }

  function applyResponse(state) {
    try {
      if (state && state.sections) {
        // --- drawer: exactly what CartNotification.updateQuantity does ----
        var drawer = document.getElementById('minicart-form');
        if (drawer && state.sections['minicart-form']) {
          var html = innerFrom(state.sections['minicart-form'], '#minicart-form');
          if (html !== null) drawer.innerHTML = html;
        }

        // --- cart page: what CartUtils.updateCartUI does ------------------
        var main = document.getElementById('main-cart-items');
        if (main && main.dataset.id && state.sections[main.dataset.id]) {
          var src = state.sections[main.dataset.id];
          if (state.item_count === 0) {
            var all = innerFrom(src, '#main-cart-items');
            if (all !== null) main.innerHTML = all;
          } else {
            var contents = main.querySelector('.js-contents');
            var newContents = innerFrom(src, '.js-contents');
            if (contents && newContents !== null) contents.innerHTML = newContents;

            var totals = document.querySelector('.cart-info .totals');
            var newTotals = innerFrom(src, '.cart-info .totals');
            if (totals && newTotals !== null) totals.innerHTML = newTotals;
          }
        }
      }

      // --- counters / progress bar / header total, as the theme does -----
      if (state && state.item_count !== undefined) {
        document.querySelectorAll('.cart-count').forEach(function (el) {
          if (el.classList.contains('cart-count-drawer')) {
            el.innerHTML = '(' + state.item_count + ')';
          } else {
            el.innerHTML = state.item_count > 100 ? '~' : state.item_count;
          }
        });
        var headerTotal = document.querySelector('header-total-price');
        if (headerTotal && typeof headerTotal.updateTotal === 'function') {
          headerTotal.updateTotal(state);
        }
      }
      if (state && state.items_subtotal_price !== undefined) {
        var bar = document.querySelector('free-ship-progress-bar');
        if (bar && typeof bar.init === 'function') bar.init(state.items_subtotal_price);
      }
      if (typeof BlsLazyloadImg !== 'undefined' && BlsLazyloadImg.init) BlsLazyloadImg.init();

      // Re-apply our UI to the freshly rendered markup.
      if (state && state.items) decorate(buildPlan(state));
    } catch (e) {
      log('applyResponse failed', e);
    } finally {
      release();
      // One confirming read, to catch a write the cart did not honour.
      schedule(250);
    }
  }

  /* ======================================================================
     UI
     ====================================================================== */

  /* Brand tokens are inherited from snippets/ovia-tokens.liquid, which is
     rendered in <head> on every template, so this blends with the drawer it
     lives in (navy #082239, sky #adc8d9, marble #e6e7e9, Manrope) rather
     than with the value-stack card on the product page. The hex fallbacks
     only apply if ovia-tokens is ever not rendered. A separate local name
     (--ovia-ui-font) is needed: redefining --ovia-font in terms of itself
     would be a self-referential cycle and the declaration would be dropped.
     Nothing is imported: no font, no external stylesheet. */
  var CSS = `
.ovia-sp,.ovia-line--gift{
  --ovia-navy:var(--ovia-dark-blue,#082239);
  --ovia-sky:var(--ovia-light-blue,#adc8d9);
  --ovia-marble:var(--ovia-grey,#e6e7e9);
  --ovia-mut:#47586a;
  --ovia-ui-font:var(--ovia-font,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif)}

.ovia-line--gift .minicart__actions,
.ovia-line--gift mini-cart-remove-button,
.ovia-line--gift .cart-item__quantity-wrapper,
.ovia-line--gift cart-remove-button{display:none!important}
.ovia-badge{display:inline-flex;align-items:center;margin-left:8px;padding:2px 8px;
  border-radius:999px;background:var(--ovia-sky);color:var(--ovia-navy);
  font-family:var(--ovia-ui-font);font-size:11px;font-weight:700;line-height:1.6;
  letter-spacing:.04em;text-transform:uppercase;vertical-align:middle}
.ovia-px{display:flex;align-items:baseline;flex-wrap:wrap;gap:8px;font-family:var(--ovia-ui-font)}
.ovia-px__was{color:var(--ovia-navy);opacity:.5;font-size:14px;text-decoration:line-through}
.ovia-px__now{color:var(--ovia-navy);font-size:16px;font-weight:700}
.ovia-px__free{padding:1px 7px;border-radius:999px;background:var(--ovia-navy);color:#fff;
  font-size:11px;font-weight:700;letter-spacing:.04em;text-transform:uppercase}
.ovia-px__save{color:var(--ovia-mut);font-size:12.5px}

/* The protection line is presented as the row below, not twice. */
.ovia-line--protection{display:none!important}

.ovia-sp{display:flex;align-items:center;gap:12px;margin:0 0 14px;padding:12px 14px;
  border:1px solid #d3e0e8;border-radius:14px;background:#fff;
  font-family:var(--ovia-ui-font);text-align:left}
.ovia-sp__main{flex:1 1 auto;min-width:0}
.ovia-sp__title{display:block;color:var(--ovia-navy);font-size:14.5px;font-weight:700;
  line-height:1.35;letter-spacing:-.01em}
.ovia-sp__note{display:block;margin-top:2px;color:var(--ovia-mut);font-size:12px;line-height:1.45}
.ovia-sp__price{flex:0 0 auto;color:var(--ovia-navy);font-size:15px;font-weight:700;
  font-variant-numeric:tabular-nums;white-space:nowrap}
.ovia-sp__switch{flex:0 0 auto;position:relative;width:46px;height:26px;padding:0;
  border:1px solid #c3d2dc;border-radius:999px;background:var(--ovia-marble);
  cursor:pointer;-webkit-appearance:none;appearance:none;
  transition:background-color .18s ease,border-color .18s ease}
.ovia-sp__switch[aria-checked='true']{background:var(--ovia-navy);border-color:var(--ovia-navy)}
.ovia-sp__knob{position:absolute;top:2px;left:2px;width:20px;height:20px;border-radius:50%;
  background:#fff;box-shadow:0 1px 3px rgba(8,34,57,.3);transition:transform .18s ease}
.ovia-sp__switch[aria-checked='true'] .ovia-sp__knob{transform:translateX(20px)}
.ovia-sp__switch:focus-visible{outline:2px solid var(--ovia-navy);outline-offset:2px}
.ovia-sp__switch:focus:not(:focus-visible){outline:none}
.ovia-sp.is-busy{opacity:.6;pointer-events:none}
@media (prefers-reduced-motion:reduce){
  .ovia-sp__switch,.ovia-sp__knob{transition:none!important}}
`;

  function injectCSS() {
    if (document.getElementById('ovia-bundle-css')) return;
    var s = document.createElement('style');
    s.id = 'ovia-bundle-css';
    s.textContent = CSS;
    (document.head || document.documentElement).appendChild(s);
  }

  /* Both line-item renderers key off a predictable id:
       drawer    snippets/cart-item.liquid   -> id="minicart__product-{key}"
       cart page sections/main-cart-items    -> id="CartItem-{index+1}"
     /cart.js returns items in the same order the templates loop in, so the
     1-based position is the cart page's index. */
  function nodesFor(item, index) {
    var out = [];
    var a = document.getElementById('minicart__product-' + item.key);
    if (a) out.push(a);
    var b = document.getElementById('CartItem-' + (index + 1));
    if (b) out.push(b);
    return out;
  }

  function decorateGift(node, gift) {
    if (node.dataset.oviaGift === '1') return;
    node.dataset.oviaGift = '1';
    node.classList.add('ovia-line--gift');

    // Badge beside the product title.
    var title = node.querySelector('.minicart__product-name a') ||
                node.querySelector('a.product-item__name');
    if (title && !title.parentNode.querySelector('.ovia-badge')) {
      var badge = document.createElement('span');
      badge.className = 'ovia-badge';
      badge.textContent = GIFT_BADGE;
      title.parentNode.insertBefore(badge, title.nextSibling);
    }

    // Price: struck compare-at, then $0, FREE, and the saving.
    var block = '<s class="ovia-px__was">' + money(gift.compareAt) + '</s>' +
                '<span class="ovia-px__now">' + money(0) + '</span>' +
                '<span class="ovia-px__free">Free</span>' +
                '<span class="ovia-px__save">Save ' + money(gift.compareAt) + '</span>';

    var drawerPrices = node.querySelector('.minicart__item-prices');
    if (drawerPrices && !drawerPrices.querySelector('.ovia-px')) {
      // Keep the theme's stepper/error nodes in the DOM (the theme's JS
      // counts [name="updates[]"]), just replace the visible price bits.
      drawerPrices.querySelectorAll(
        '.minicart__discounted-prices,.minicart__item-price,.minicart__item-original-price'
      ).forEach(function (el) { el.remove(); });
      var d = document.createElement('div');
      d.className = 'ovia-px';
      d.innerHTML = block;
      drawerPrices.insertBefore(d, drawerPrices.firstChild);
    }

    node.querySelectorAll('.cart__page-price .cart-item__price-wrapper').forEach(function (w) {
      if (w.querySelector('.ovia-px')) return;
      w.innerHTML = '<div class="ovia-px">' + block + '</div>';
    });

    // Not shopper-editable: hidden by CSS, and disabled so neither a click
    // nor a keyboard tab can reach them.
    node.querySelectorAll('input[name="updates[]"],.quantity__button').forEach(function (el) {
      el.disabled = true;
      el.setAttribute('aria-hidden', 'true');
      el.tabIndex = -1;
    });
    node.querySelectorAll('mini-cart-remove-button a,cart-remove-button a').forEach(function (el) {
      el.setAttribute('aria-hidden', 'true');
      el.tabIndex = -1;
      el.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); });
    });
  }

  function setSwitch(row, on) {
    var sw = row.querySelector('.ovia-sp__switch');
    if (sw) sw.setAttribute('aria-checked', on ? 'true' : 'false');
    row.classList.remove('is-busy');
  }

  function onToggle(e) {
    var sw = e.currentTarget;
    var row = sw.closest('.ovia-sp');
    var turningOn = sw.getAttribute('aria-checked') !== 'true';
    setProtectionOff(!turningOn);
    sw.setAttribute('aria-checked', turningOn ? 'true' : 'false');
    if (row) row.classList.add('is-busy');
    // Re-arm the brakes: this is a deliberate shopper action, not a loop.
    halted = false;
    identicalWrites = 0;
    lastSignature = '';
    schedule(0);
  }

  function renderProtection(plan) {
    var host = document.querySelector('#minicart-form .drawer-bottom');
    var existing = document.getElementById('ovia-sp-row');

    if (!plan.pergola || !host) {
      if (existing) existing.remove();
      return;
    }
    if (existing && existing.parentNode === host) {
      setSwitch(existing, !protectionOff());   // update in place, keeps focus
      return;
    }
    if (existing) existing.remove();

    var hadFocus = document.activeElement &&
                   document.activeElement.classList &&
                   document.activeElement.classList.contains('ovia-sp__switch');

    var row = document.createElement('div');
    row.id = 'ovia-sp-row';
    row.className = 'ovia-sp';
    row.innerHTML =
      '<span class="ovia-sp__main">' +
        '<span class="ovia-sp__title"></span>' +
        '<span class="ovia-sp__note"></span>' +
      '</span>' +
      '<span class="ovia-sp__price"></span>' +
      '<button type="button" class="ovia-sp__switch" role="switch" aria-checked="true">' +
        '<span class="ovia-sp__knob" aria-hidden="true"></span>' +
      '</button>';

    row.querySelector('.ovia-sp__title').textContent = PROTECTION.title;
    row.querySelector('.ovia-sp__note').textContent = PROTECTION.note;
    row.querySelector('.ovia-sp__price').textContent = money(PROTECTION.price);

    var sw = row.querySelector('.ovia-sp__switch');
    sw.setAttribute('aria-label', PROTECTION.title + ', ' + money(PROTECTION.price));
    sw.addEventListener('click', onToggle);

    host.insertBefore(row, host.firstChild);
    setSwitch(row, !protectionOff());
    if (hadFocus) sw.focus();
  }

  function decorate(plan) {
    try {
      injectCSS();
      plan.items.forEach(function (item, index) {
        var gift = GIFT_BY_VARIANT[item.variant_id];
        var isProtection = item.variant_id === PROTECTION.variant;
        if (!gift && !isProtection) return;
        nodesFor(item, index).forEach(function (node) {
          if (gift) decorateGift(node, gift);
          else node.classList.add('ovia-line--protection');
        });
      });
      renderProtection(plan);
    } catch (e) {
      log('decorate failed', e);
    }
  }

  /* ======================================================================
     Triggers
     ====================================================================== */

  /* The theme replaces innerHTML of #minicart-form (drawer) and of
     .js-contents inside #main-cart-items (cart page) after every cart
     change. Watching those two containers both re-applies our UI and tells
     us the cart moved, whoever moved it. */
  function observe() {
    if (!('MutationObserver' in window)) return;
    var obs = new MutationObserver(function () {
      if (inFlight) return;          // ignore the churn from our own write
      schedule(150);
    });
    ['minicart-form', 'main-cart-items'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) obs.observe(el, { childList: true, subtree: true });
    });
    // The drawer container itself is re-rendered in the theme editor.
    var note = document.querySelector('cart-notification');
    if (note) obs.observe(note, { childList: true });
  }

  /* A passive tap on fetch, so a cart change that does not re-render the
     containers above (an app, a page without the drawer) still triggers a
     sync. It forwards every call through untouched - no request is rewritten
     - and only schedules work once the response has landed. */
  function tapFetch() {
    var orig = window.fetch;
    if (typeof orig !== 'function' || orig.__oviaTapped) return;
    var wrapped = function (input, init) {
      var url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) {}
      var mine = false;
      try {
        var h = (init && init.headers) || (input && input.headers);
        if (h) mine = !!(h['X-Ovia-Bundle'] || (h.get && h.get('X-Ovia-Bundle')));
      } catch (e) {}
      var p = orig.apply(this, arguments);
      if (!mine && /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/.test(url)) {
        p.then(function () { schedule(200); }, function () {});
      }
      return p;
    };
    wrapped.__oviaTapped = true;
    window.fetch = wrapped;
  }

  /* ======================================================================
     The PDP contract

     blocks/_product-price.liquid sync() does:

       if (!vid || vid === last || !window.oviaShadeSize) return;
       last = vid;
       var s = window.oviaShadeSize(vid);
       if (!s) return;
       ... el.textContent = s + "'"  ...  s === 13 ? '$1,180' : '$980'

     So it wants a function of a PERGOLA VARIANT ID returning the NUMBER 10
     or 13. The test it runs is s === 13, which is strict, so handing back a
     string would silently price every size at $980. ovia-free-shade used to
     define this from a Liquid-rendered id -> option1 map; this file is a
     static .js asset with no Liquid, so the map comes from the product's own
     .js endpoint instead.

     Two deliberate details:
       - The function is published only once the map is in hand. sync() skips
         itself while window.oviaShadeSize is missing and re-polls every
         800ms, so arriving late costs nothing. But it assigns last = vid
         BEFORE it checks the result, so publishing a function that returns
         null would poison that variant until the shopper picked another.
       - Nothing here listens for variant changes. It does not need to: the
         block already polls and listens, and this function is pure.
     ====================================================================== */
  function publishShadeContract() {
    var m = /^(.*\/products\/[^\/?#]+)/.exec(location.pathname);
    if (!m) return;                       // not a product page
    fetch(m[1] + '.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json', 'X-Ovia-Bundle': '1' }
    }).then(function (r) { return r.json(); }).then(function (p) {
      /* Same single source of truth as the cart side. */
      if (!p || TRIGGER_PRODUCT_IDS.indexOf(p.id) === -1) return;
      var sizeById = {};
      (p.variants || []).forEach(function (v) { sizeById[v.id] = v.option1; });

      window.oviaShadeSize = function (variantId) {
        var raw = sizeById[parseInt(variantId, 10)];
        if (raw === undefined) return null;
        return sizeFromText(raw) || SHADE_DEFAULT_SIZE;
      };
      /* Part of the same published surface. Nothing in the theme reads it
         today, but ovia-free-shade exposed it, so keep it pointing at the
         right gift variant rather than leaving a stale global behind. */
      window.oviaShadeFor = function (variantId) {
        var s = window.oviaShadeSize(variantId);
        return s ? SHADE[s].variant : null;
      };
      log('shade contract published for product', p.id);
    }).catch(function (e) { log('shade contract unavailable', e); });
  }

  function start() {
    injectCSS();
    tapFetch();
    publishShadeContract();
    observe();
    schedule(0);
    document.addEventListener('shopify:section:load', function () {
      observe();
      schedule(200);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
